import { decodeBase64Url, encodeBase64Url, toArrayBuffer } from './encoding.js';
import { getPasskeyAuthenticationFromOptions, parsePasskeyAuthenticationRequest } from './passkeys.js';
function requireSubtleCrypto() {
    if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') {
        throw new Error('WebCrypto is not available in this environment.');
    }
    return crypto.subtle;
}
function toPrfValues(input) {
    return {
        first: toArrayBuffer(decodeBase64Url(input.first)),
        second: input.second === undefined ? undefined : toArrayBuffer(decodeBase64Url(input.second))
    };
}
function toPrfCredentialMap(allowCredentials, prf) {
    if (allowCredentials === undefined || allowCredentials.length === 0) {
        throw new Error('Authenticator PRF authentication requires at least one allowed credential.');
    }
    const prfValues = toPrfValues(prf);
    const evalByCredential = {};
    for (const credential of allowCredentials) {
        evalByCredential[encodeBase64Url(toArrayBuffer(credential.id))] = prfValues;
    }
    return evalByCredential;
}
export function parseAuthenticatorPrfRequest(request) {
    const publicKey = parsePasskeyAuthenticationRequest(request.publicKey);
    publicKey.extensions = {
        ...publicKey.extensions,
        prf: {
            evalByCredential: toPrfCredentialMap(publicKey.allowCredentials, request.prf)
        }
    };
    return {
        publicKey
    };
}
export async function getAuthenticatorPrf(request) {
    const options = parseAuthenticatorPrfRequest(request);
    const publicKey = options.publicKey;
    if (publicKey === undefined) {
        throw new Error('Authenticator PRF request did not produce public key options.');
    }
    const result = await getPasskeyAuthenticationFromOptions(publicKey);
    const prf = result.clientExtensionResults.prf?.results;
    if (prf === undefined) {
        throw new Error('Authenticator PRF results were not returned by this credential.');
    }
    return {
        ...result,
        prf
    };
}
export async function importSymmetricKey(request) {
    const subtle = requireSubtleCrypto();
    return await subtle.importKey('raw', toArrayBuffer(decodeBase64Url(request.keyMaterial)), {
        name: request.algorithm ?? 'AES-GCM'
    }, request.extractable ?? false, request.usages ?? ['encrypt', 'decrypt']);
}
export async function deriveSymmetricKeyFromPrf(request) {
    const result = await getAuthenticatorPrf(request);
    return await importSymmetricKey({
        keyMaterial: result.prf.first
    });
}
